<template>
  <div class="hosts-dashboard-content">
    <el-row :gutter="20" v-if="agent && agent.containers.length > 0">
      <el-col :span="24">
        <el-row>
          <el-col :span="4">
            <div class="scroll-menu">
              <h5>Containers</h5>
              <el-link
                v-for="container in agent.containers"
                :key="container.containerId"
                :underline="false"
                @click="scrollToElementID('containers', container.containerId)"
              >
                <el-tooltip
                  effect="dark"
                  placement="top-start"
                  popper-class="containername-tooltip"
                  :content="getContainerName(container)"
                  :enterable="false"
                >
                  <span>{{ getContainerName(container) }}</span>
                </el-tooltip>
              </el-link>
            </div>
            &nbsp;
          </el-col>
          <el-col :span="20">
            <el-collapse
              v-model="activeContainersCollapse"
              v-for="container in agent.containers"
              :key="container.containerId"
              :id="container.containerId"
            >
              <el-collapse-item :id="container.containerId" :name="container.containerId">
                <template slot="title">
                  {{ getContainerName(container) }} |
                  {{ container.containerConfig.configPrivileged ? 'privileged' : 'unprivileged' }}
                </template>
                <el-row :gutter="20">
                  <el-col :span="4" class="field"> Container ID </el-col>
                  <el-col :span="20" class="content"> {{ container.containerId }} </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :span="4" class="field"> Image ID </el-col>
                  <el-col :span="20" class="content"> {{ container.containerImageId }} </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :span="4" class="field"> Start Date </el-col>
                  <el-col :span="20" class="content">
                    {{ formatDateTimeNS(container.containerStartTime) }}
                  </el-col>
                </el-row>
                <el-divider></el-divider>
                <el-row :gutter="20">
                  <el-col :span="4" class="field"> Network Mode </el-col>
                  <el-col :span="20" class="content"> {{ container.containerConfig.configNetworkMode }} </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :span="4" class="field"> IP Address </el-col>
                  <el-col :span="20" class="content"> {{ container.containerConfig.configIpAddress }} </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :span="4" class="field"> Priviledged </el-col>
                  <el-col :span="20" class="content"> {{ container.containerConfig.configPrivileged }} </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :span="4" class="field"> Path </el-col>
                  <el-col :span="20" class="content"> {{ container.containerConfig.configPath }} </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :span="4" class="field"> Environment </el-col>
                  <el-col :span="20" class="content">
                    <div v-for="(item, idx) in container.containerConfig.configEnv" :key="idx">{{ item }}</div>
                  </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :span="4" class="field"> Args </el-col>
                  <el-col :span="20" class="content">
                    <div v-for="(item, idx) in container.containerConfig.configArgs" :key="idx">{{ item }}</div>
                  </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :span="4" class="field"> Mounts </el-col>
                  <el-col :span="20" class="content">
                    <div v-for="(item, idx) in container.containerConfig.configMounts" :key="idx">{{ item }}</div>
                  </el-col>
                </el-row>
              </el-collapse-item>
            </el-collapse>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment-timezone'
import PageHeaders from '@/components/layout/PageHeaders.vue'
import Stats from '@/views/hosts/Stats.vue'

import {
  DATETIME_FORMAT_LONG,
  NANOSECONDS_TO_MILLISECONDS,
  INSIGHTS_DATE_RANGE_CHOICES
} from '@/constants/datetime.constants'
import { getContainerNameLabel } from '@/utilities/agent.helper'
import DateRangeDropdown from '@/components/dropdown/DateRangeDropdown.vue'

import { AGENT_DETAILS_REQ } from '@/store/modules/agents/agents.type'
import { HOST_INSIGHTS_DATE_RANGE_PREFERENCE } from '@/store/modules/user_pref/user-pref.type'

export default {
  name: 'ContainerDetails',
  data () {
    return {
      daterangeUserPrefConstant: HOST_INSIGHTS_DATE_RANGE_PREFERENCE,
      dateRangeOptions: INSIGHTS_DATE_RANGE_CHOICES,
      agendId: '',
      customHostname: '',
      breadcrumbs: [{ title: 'Dashboard' }, { title: 'Hosts', to: '/assets/hosts' }],
      agent: null,
      warning: false,
      activeDetectionsCollapse: [],
      activeDetailsCollapse: ['host-details', 'host-agent', 'host-network', 'host-configuration'],
      activeInsightsCollapse: [],
      activeContainersCollapse: [],
      paneHeightStyle: {
        height: '500px'
      }
    }
  },
  created () {
    this.resize()
    this.agentId = this.$route.params.agentId

    // always load tab details
    this.loadTabDetails()

    // add resize listener to window
    window.addEventListener('resize', (this.resizeListener = this.resize.bind(this)))
  },
  mounted () {
    this.loadView(this.activeTab)
  },
  beforeDestroy () {
    // remove resize listener from window
    window.removeEventListener('resize', this.resizeListener)
  },
  methods: {
    resize () {
      let OFFSET_TOP_DISTANCE = 172
      let tabPaneHeight = window.innerHeight - OFFSET_TOP_DISTANCE

      this.paneHeightStyle.height = tabPaneHeight + 'px'
    },
    switchView (tab, event) {
      this.$router.replace({
        query: {
          view: tab.paneName
        }
      })
      this.loadView(tab.paneName)
    },
    loadView (name) {
      this.loadTabDetails()
    },
    loadTabDetails () {
      this.$store.dispatch(AGENT_DETAILS_REQ, this.agentId).then(
        agent => {
          this.agent = agent
          this.agent.publicIPs = this.agent.publicIPs.sort()
          this.agent.privateIPs = this.agent.privateIPs.sort()
          this.customHostname = this.agent.customHostname
          if (this.breadcrumbs.length === 2) {
            this.breadcrumbs.push({ title: this.customHostname })
          }
          if (this.agent.aggregatedRiskScore > 0) {
            this.warning = 'at risk'
          }
          if (this.agent.containers.length > 0) {
            this.agent.containers.forEach(container => {
              this.activeContainersCollapse.push(container.containerId)
            })
          }
        },
        errMsg => {
          const h = this.$createElement
          this.$msgbox({
            title: 'Details Error',
            message: h('p', null, 'There was an error loading the agent you requested.'),
            showCancelButton: false,
            confirmButtonText: 'Ok',
            beforeClose: (action, instance, done) => {
              window.location.href = '/#/hosts'
            }
          })
        }
      )
    },
    loadTabInsights () {
      this.$refs.dns.fetchHostInsights()
      this.$refs.ips.fetchHostInsights()
      this.$refs.logins.fetchHostInsights()
      this.$refs.powershell.fetchHostInsights()
    },
    insightsUpdated (update) {
      let name = update.name
      let actives = this.activeInsightsCollapse
      if (update.hasChartData) {
        actives.push(name)
      }
    },
    formatDateTime (datetime) {
      return moment(datetime).format(DATETIME_FORMAT_LONG)
    },
    formatDateTimeNS (datetime) {
      return this.formatDateTime(datetime * NANOSECONDS_TO_MILLISECONDS)
    },
    getContainerName (container) {
      let containerName = `${container.containerType.toLowerCase()} | `
      if (_.isArray(container.containerNames)) {
        container.containerNames.forEach((name, indx) => {
          containerName += `${getContainerNameLabel(name)}`
          if (indx !== container.containerNames.length - 1) {
            containerName += ' | '
          }
        })
        return containerName
      }
      return containerName + getContainerNameLabel(container.containerNames)
    },
    scrollToElementID (pane, id) {
      let OFFSET_TOP_DISTANCE = 169

      let val
      let tabs = this.$el
      let element = document.getElementById(id)
      let rect = element.getBoundingClientRect()
      let to = rect.y - OFFSET_TOP_DISTANCE + tabs.scrollTop

      let start = tabs.scrollTop
      let change = to - start
      let currentTime = 0
      let increment = 20
      let duration = 250
      let easeInOutQuad = (t, b, c, d) => {
        t /= d / 2
        if (t < 1) return (c / 2) * t * t + b
        t--
        return (-c / 2) * (t * (t - 2) - 1) + b
      }
      let animateScroll = () => {
        currentTime += increment
        val = easeInOutQuad(currentTime, start, change, duration)
        tabs.scrollTop = val
        if (currentTime < duration) {
          setTimeout(animateScroll, increment)
        }
      }
      animateScroll()
    }
  },
  computed: {
    ...mapGetters([
      'insightsEnabled',
      'agentDetailsReqStatus',
      'hostInsightsDateRangeUserSetting',
      'hrlyDnsRequestStatisticsReqStatus',
      'hrlyExternalIpStatisticsReqStatus',
      'hrlyLoginFailuresStatisticsReqStatus',
      'hrlyPrivilegedExecStatisticsReqStatus'
    ])
  },
  watch: {
    agentDetailsReqStatus: {
      handler: (newStatus, oldStatus) => {
        if (newStatus === 'error') {
          console.log('error')
        }
      }
    },
    hostInsightsDateRangeUserSetting: {
      handler: function (newDateRange, oldDateRange) {
        if (newDateRange !== oldDateRange) {
          this.loadView(this.activeTab)
        }
      }
    }
  },
  components: {
    PageHeaders,
    Stats,
    DateRangeDropdown
  }
}
</script>

<style scoped>
.hosts-dashboard-content {
  overflow: scroll;
  height: calc(100% - 145px);
}

.scroll-menu {
  position: fixed;
  width: inherit;

  >>> .el-link {
    width: 80%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.scroll-menu h5 {
  margin-top: 0.5em;
  margin-bottom: 1.5em;
}
.scroll-menu a {
  display: block;
  margin: 0.5em 0;
}

>>> .el-collapse-item__header {
  height: 36px;
  padding-left: 15px;
  font-weight: 700;
  background: #f4f4f4;
}

>>> .el-collapse-item__content {
  padding: 15px;

  .name {
    color: #999999;
    font-size: 12px;
    font-weight: bold;
  }

  .el-row {
    margin-bottom: 5px;
  }
}
</style>

<style>
.containername-tooltip {
  .popper__arrow {
    left: 50px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/hosts/ContainerDetails.vue